import { isIncomeStreamAssetTypeId, type AssetTypeId } from '@/features/funding-sources';

interface ApplyAssetTypeChangeArgs {
  prev: SelfFundingSource;
  prevType: AssetTypeId | 'other';
  newType: AssetTypeId | 'other';
  ltcAtAge: number | null | undefined;
}

interface ApplyAssetTypeChangeResult {
  /** Updated `selfFundingState` with fields rearranged for the new type. */
  state: SelfFundingSource;
  /** When set, also update the `yearlyContributionValue` display state. */
  yearlyContributionUpdate?: number | null;
}

/**
 * Switching the asset-type dropdown rearranges field values so the user
 * doesn't lose data when they pivot between categories:
 *   - Income Stream → Other: annual income → monthly income / tax → income tax.
 *   - Other → Income Stream: monthly income * 12 → annual income / income tax → tax.
 *   - Investment → Income Stream: seed `incomeReferenceAge` to LTC start age.
 *   - Specific (non-income-stream): rebuild the yearly-contribution display from
 *     the stored monthly amount.
 */
export function applyAssetTypeChange({
  prev,
  prevType,
  newType,
  ltcAtAge,
}: ApplyAssetTypeChangeArgs): ApplyAssetTypeChangeResult {
  const prevIsIncomeStream = prevType !== 'other' && isIncomeStreamAssetTypeId(prevType);
  const newIsIncomeStream = newType !== 'other' && isIncomeStreamAssetTypeId(newType);

  if (prevIsIncomeStream && newType === 'other') {
    return {
      state: {
        ...prev,
        monthlyIncome: prev.existingAssetsValue != null ? prev.existingAssetsValue / 12 : null,
        monthlyIncomeTaxRate: prev.gainsTaxRate ?? null,
        existingAssetsValue: null,
        gainsTaxRate: null,
        incomeReferenceAge: null,
      },
    };
  }

  if (prevType === 'other' && newIsIncomeStream) {
    return {
      state: {
        ...prev,
        existingAssetsValue: prev.monthlyIncome != null ? prev.monthlyIncome * 12 : null,
        gainsTaxRate: prev.monthlyIncomeTaxRate ?? null,
        monthlyIncome: null,
        monthlyIncomeTaxRate: null,
        incomeReferenceAge: prev.incomeReferenceAge ?? ltcAtAge ?? null,
      },
    };
  }

  if (!prevIsIncomeStream && newIsIncomeStream) {
    return {
      state: {
        ...prev,
        incomeReferenceAge: prev.incomeReferenceAge ?? ltcAtAge ?? null,
      },
    };
  }

  if (newType !== 'other' && !newIsIncomeStream) {
    return {
      state: prev,
      yearlyContributionUpdate: prev.monthlyContributionAmount
        ? prev.monthlyContributionAmount * 12
        : null,
    };
  }

  return { state: prev };
}
